import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../guards/auth.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly loginUrl = `${environment.baseUrl}/auth/login`;
  private readonly logoutUrl = `${environment.baseUrl}/auth/logout`;
  private readonly refreshUrl = `${environment.baseUrl}/auth/refresh`;

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();
    const authReq = token && this.shouldAttachToken(req.url) ? this.addToken(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Reactive: gặp 401 thì làm mới token rồi thử lại. Đây là lưới an toàn bên cạnh
        // silent-renew nền của AuthService. Login/refresh/logout tự chịu trách nhiệm lỗi của mình
        // để tránh đệ quy refresh khi chính request refresh trả về 401.
        if (error.status === 401 && this.canRefresh(req.url)) {
          return this.handle401Error(req, next);
        }
        if (error.status === 403) {
          console.error('Forbidden: không có quyền truy cập tài nguyên này');
          this.router.navigate(['/forbidden']).then();
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Không còn refresh token -> khỏi gọi API, đưa về login ngay.
    if (!this.authService.hasRefreshToken()) {
      this.forceLogout();
      return throwError(() => new Error('Phiên đăng nhập đã hết hạn'));
    }

    // refreshToken() là single-flight: nhiều request 401 đồng thời (và timer nền) dùng chung 1 lần gọi.
    return this.authService.refreshToken().pipe(
      take(1),
      catchError(err => {
        // Refresh thất bại -> đăng xuất, về login và ném lỗi cho request đang chờ.
        this.forceLogout();
        return throwError(() => err);
      }),
      switchMap(auth => next.handle(this.addToken(request, auth.accessToken)))
    );
  }

  /** Bỏ qua access token cho request công khai (tải cấu hình/i18n) và request auth. */
  private shouldAttachToken(url: string): boolean {
    if (url === this.loginUrl || url === this.refreshUrl) {
      return false;
    }
    const path = url.split('?')[0];
    if (path.endsWith('/ecm/download/system') || path.endsWith('/files/download/system')) {
      return false;
    }
    return true;
  }

  /** Request được phép kích hoạt refresh khi gặp 401 (loại trừ chính các request auth). */
  private canRefresh(url: string): boolean {
    return url !== this.loginUrl && url !== this.refreshUrl && url !== this.logoutUrl;
  }

  private forceLogout(): void {
    this.authService.removeToken();
    if (!this.router.url.startsWith('/login')) {
      this.router.navigate(['/login']).then();
    }
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-Code': this.authService.getBuilding(),
        'X-Role-Code': this.authService.getRoleSelect()
      }
    });
  }
}
